<script setup>

import {
  add,
  always, cond, divide, equals, isNil, isNotNil, multiply, path, pipe, prop, subtract,
} from 'ramda';
import {
  computed, ref, toValue, watch,
} from 'vue';
import Price from '@/components/Price.vue';
import { isIndividualPlan, isNotNullAndNotEmpty } from '@/utilities';
import useCalculatePrice from '@/services/pricing';
import WithLoader from '@/components/WithLoader.vue';

const props = defineProps({
  chosenPlan: {
    required: true,
  },
  numberSeats: {
    required: true,
  },
  vat: {
    required: true,
  },
  appliedDiscount: {
    required: true,
  },
});

const emit = defineEmits(['price-updated']);

const {
  fetch, response, isLoading: isCalculatingPrice,
} = useCalculatePrice();

const isIndividual = computed(() => isIndividualPlan(toValue(props.chosenPlan)));
const standardPrice = computed(() => multiply(props.numberSeats, toValue(isIndividual) ? props.chosenPlan.subscriptionPrice : props.chosenPlan.seatPrice));
const appliedVat = props.vat;
const ceilToTwoDecimals = (value) => Math.round(value * 100) / 100;
const vatAmount = computed(() => ceilToTwoDecimals(multiply(standardPrice.value, appliedVat)));
const totalPrice = ref(ceilToTwoDecimals(multiply(standardPrice.value, (1 + appliedVat))));

const isLoadingPrice = computed(() => toValue(isCalculatingPrice) || toValue(seatChangeTimeout) > 0);

const seatChangeTimeout = ref(0);

const hasDiscountApplied = computed(() => isNotNullAndNotEmpty(toValue(props.appliedDiscount)));

const discountTypeForPricing = computed(() => cond([
  [equals('discountAmount'), always('money')],
  [equals('discountPercentage'), always('percent')],
])(toValue(props.appliedDiscount).type));

const recalculatePrice = async () => {
  const calculateVatReduction = (discount) => divide(discount, add(1, appliedVat));
  if (!hasDiscountApplied.value) {
    totalPrice.value = ceilToTwoDecimals(multiply(standardPrice.value, add(1, appliedVat)));
    vatAmount.value = ceilToTwoDecimals(multiply(standardPrice.value, appliedVat));
    return;
  }
  await fetch(props.chosenPlan.planId, props.numberSeats, props.appliedDiscount.key);

  const totalWithDiscount = prop('total', toValue(response));
  const discountAmount = toValue(props.appliedDiscount.discount);

  totalPrice.value = pipe(
    cond([
      [equals('percent'), always(totalWithDiscount)],
      [equals('money'),
        () => add(totalWithDiscount, subtract(discountAmount, calculateVatReduction(discountAmount))),
      ],
    ]),
    ceilToTwoDecimals,
  )(discountTypeForPricing.value);
  vatAmount.value = ceilToTwoDecimals(subtract(totalPrice.value, totalWithDiscount));

  emit('price-updated', {
    totalPrice: totalPrice.value,
    vatAmount: vatAmount.value,
    hasDiscount: hasDiscountApplied.value,
  });
};

watch(() => props.appliedDiscount, recalculatePrice);

watch(() => props.appliedDiscount, () => {
  recalculatePrice();
});

watch(() => props.numberSeats, () => {
  if (!toValue(hasDiscountApplied)) {
    recalculatePrice();
    return;
  }

  // buffer changes before making the final request
  const bufferTime = 1000;

  if (isNotNil(seatChangeTimeout)) {
    clearTimeout(seatChangeTimeout.value);
    seatChangeTimeout.value = 0;
  }

  seatChangeTimeout.value = setTimeout(() => {
    recalculatePrice();
    seatChangeTimeout.value = 0;
  }, bufferTime);
});

watch(() => props.chosenPlan, () => {
  recalculatePrice();
});

console.log('VAT in pricing component', props.vat);

</script>

<template>
  <template v-if="hasDiscountApplied">
    <div class="flex items-center font-bold justify-between border-t border-white border-opacity-10 pt-6 mt-3">
      <i18n-t
        tag="dt"
        class=""
        keypath="registerView.billing.yearlyTotalPrice"
      >
        <span>{{ numberSeats }}</span>
        <span>{{ isIndividual ? chosenPlan.subscriptionPrice : chosenPlan.seatPrice }}</span>
      </i18n-t>

      <dd class="text-2xl line-through">
        <Price :price="standardPrice" />
      </dd>
    </div>
    <div class="flex items-center font-bold justify-between pt-6 mt-3">
      <dt>
        {{ $t('registerView.billing.discount') }}
      </dt>
      <dd>
        <Price
          :price="appliedDiscount.discount"
          sign="-"
          :type="discountTypeForPricing"
        />
      </dd>
      <dt />
      <dd class="text-xl">
        <Price :price="totalPrice" />
      </dd>
    </div>

    <div class="flex items-center font-bold justify-between pt-6 mt-3">
      <dt>{{ $t('registerView.billing.vatLabel') }} ({{ appliedVat * 100 }}%)</dt>
      <dd>
        <Price :price="(totalPrice*(1+appliedVat)-totalPrice)" />
      </dd>
    </div>

    <div class="flex items-center font-bold justify-between border-t border-white border-opacity-10 pt-6 mt-3">
      <dt>Total</dt>

      <dd class="text-2xl">
        <WithLoader :is-loading="isLoadingPrice">
          <Price :price="(totalPrice*(1+appliedVat))" />
        </WithLoader>
      </dd>
    </div>
  </template>

  <template v-else>
    <div class="flex items-center font-bold justify-between pt-6 mt-3">
      <dt>{{ $t('registerView.billing.vatLabel') }} ({{ appliedVat * 100 }}%)</dt>
      <dd>
        <Price :price="vatAmount" />
      </dd>
    </div>
    <div class="flex items-center font-bold justify-between border-t border-white border-opacity-10 pt-6 mt-3">
      <i18n-t
        tag="dt"
        class=""
        keypath="registerView.billing.yearlyTotalPrice"
      >
        <span>{{ numberSeats }}</span>
        <span>{{ isIndividual ? chosenPlan.subscriptionPrice : chosenPlan.seatPrice }}</span>
      </i18n-t>

      <dd class="text-2xl">
        <WithLoader :is-loading="isLoadingPrice">
          <Price :price="totalPrice" />
        </WithLoader>
      </dd>
    </div>
  </template>
</template>
